import { getCampaignParameters } from '@/components/molecules/CampaignManager'
import {
  FIELDNAMES,
  KNOWN_FORM_VALUES,
} from '@/components/molecules/Form/utils/constants'
import {
  createNestedObject,
  formatDayMonthForApi,
  isNumeric,
} from '@/utils/paymentUtils'
import { SERVER_PAYMENTS_METHODS } from '@/utils/types'

const {
  DONATION_TYPE,
  DONATION_FREQUENCY,
  DONATION_AMOUNT,
  DONATION_IN_MEMORY_RECEIVER_NAME,
  DONATION_IN_MEMORY_RECEIVER_ANNIVERSARY_DATE,
  DONATION_IN_MEMORY_LETTER_TO_RELATIVES,
  DONATOR_TYPE,
  DONATOR_FIRST_NAME,
  DONATOR_LAST_NAME,
  DONATOR_GENDER,
  DONATOR_EMAIL,
  DONATOR_TAX_CODE,
  DONATOR_CELLPHONE,
  DONATOR_COMPANY_NAME,
  DONATOR_COMPANY_TAX_CODE,
  DONATOR_COMPANY_EMAIL,
  DONATOR_COMPANY_TELEPHONE,
  DONATOR_ADDRESS,
  DONATOR_ADDRESS_NUMBER,
  DONATOR_COUNTRY,
  DONATOR_PROVINCE,
  DONATOR_CITY,
  DONATOR_POSTAL_CODE,
  DONATION_PAYMENT_METHOD_ON_RECURRENCE,
  DONATION_PAYMENT_METHOD_ON_RECURRENCE_IBAN,
  DONATION_PAYMENT_METHOD_ON_RECURRENCE_IBAN_IS_OWNER,
  DONATION_IBAN_OWNER_FIRST_NAME,
  DONATION_IBAN_OWNER_LAST_NAME,
  DONATION_IBAN_OWNER_TAX_CODE,
  DONATOR_LETTER_FORMAT,
  DONATOR_PAYMENT_METHOD,
  DONATION_LETTER_PARTICIPANTS,
  DONATION_LETTER_RECIPIENT_COURTESY_TITLE,
  DONATION_LETTER_RECIPIENT_NAME,
  DONATION_LETTER_RECIPIENT_EMAIL,
  DONATION_PRIVACY,
  DONATION_TERMS,
  DONATION_NEWSLETTER,
  DONATION_LETTER_RECIPIENT_ADDRESS,
  DONATION_LETTER_RECIPIENT_ADDRESS_NUMBER,
  DONATION_LETTER_RECIPIENT_POSTAL_CODE,
  DONATION_LETTER_RECIPIENT_CITY,
  DONATION_LETTER_RECIPIENT_PROVINCE,
  DONATION_LETTER_RECIPIENT_COUNTRY,
  DONATION_PAYMENT_METHOD_ON_RECURRENCE_PAYMENT_TYPE,
} = FIELDNAMES

const {
  CUSTOM_DONATION_AMOUNT,
  DEFAULT,
  DIGITAL,
  FREE_DONATION_TYPE,
  IN_MEMORY_DONATION_TYPE,
  INDIVIDUAL,
  COMPANY,
  MALE,
  FEMALE,
  ONE_TIME_DONATION_FREQUENCY,
  MONTHLY_DONATION_FREQUENCY,
  YEARLY_DONATION_FREQUENCY,
  YES,
  NO,
  TRUE,
  FALSE,
  PAPER,
  GENERIC_DONATION_TYPE,
  PEDIATRIC_DONATION_TYPE,
} = KNOWN_FORM_VALUES

/**
 * Transforms form data into API-expected format
 */
export function formatDataToSend(
  inputData,
  paymentClientId,
  serverPaymentMethod,
  searchParams
) {
  // Query wins per key. Form values (campaign etsDefaults already resolved
  // into the form) are only the fallback, so a landing-link segment stays
  // on top of the segment baked into the form.
  const campaignParams = getCampaignParameters(
    process.env.cookieName || 'none',
    {
      ets_cmmk: inputData?.ets_cmmk,
      ets_sgmt: inputData?.ets_sgmt,
      ets_sggt: inputData?.ets_sggt,
      ets_sggt_t: inputData?.ets_sggt_t,
    },
    searchParams
  )

  const data = {
    ...inputData,
    donatorPaymentMethod: serverPaymentMethod,
  }

  const fieldNameMap = {
    [DONATION_TYPE]: 'donationtype',
    [DONATION_FREQUENCY]: 'frequency',
    [DONATION_AMOUNT]: 'amount',
    [DONATION_IN_MEMORY_RECEIVER_NAME]: 'deceased',
    [DONATION_IN_MEMORY_RECEIVER_ANNIVERSARY_DATE]: 'data_anniversario',
    [DONATION_IN_MEMORY_LETTER_TO_RELATIVES]: 'sendletter',
    [DONATOR_TYPE]: 'usertype',
    [DONATOR_FIRST_NAME]: 'firstname',
    [DONATOR_LAST_NAME]: 'lastname',
    [DONATOR_GENDER]: 'gender',
    [DONATOR_EMAIL]: 'email',
    [DONATOR_TAX_CODE]: 'fiscalcode',
    [DONATOR_CELLPHONE]: 'mobilephone',
    [DONATOR_COMPANY_NAME]: 'companyname',
    [DONATOR_COMPANY_TAX_CODE]: 'taxid',
    [DONATOR_COMPANY_EMAIL]: 'companyemail',
    [DONATOR_COMPANY_TELEPHONE]: 'phone',
    [DONATOR_ADDRESS]: 'billingaddress[address]',
    [DONATOR_ADDRESS_NUMBER]: 'billingaddress[number]',
    [DONATOR_COUNTRY]: 'billingaddress[country]',
    [DONATOR_PROVINCE]: 'billingaddress[state]',
    [DONATOR_CITY]: 'billingaddress[city]',
    [DONATOR_POSTAL_CODE]: 'billingaddress[postalcode]',
    [DONATION_PAYMENT_METHOD_ON_RECURRENCE]: 'paymentContinuslyType',
    [DONATION_PAYMENT_METHOD_ON_RECURRENCE_IBAN]: 'bonificIban',
    [DONATION_PAYMENT_METHOD_ON_RECURRENCE_IBAN_IS_OWNER]: 'bonificAccount',
    [DONATION_IBAN_OWNER_FIRST_NAME]: 'bonificFirstname',
    [DONATION_IBAN_OWNER_LAST_NAME]: 'bonificLastname',
    [DONATION_IBAN_OWNER_TAX_CODE]: 'bonificFiscalCode',
    [DONATOR_LETTER_FORMAT]: 'letter[type]',
    [DONATOR_PAYMENT_METHOD]: 'payment-method',
    [DONATION_LETTER_PARTICIPANTS]: 'letter[senders]',
    [DONATION_LETTER_RECIPIENT_COURTESY_TITLE]: 'letter[title]',
    [DONATION_LETTER_RECIPIENT_NAME]: 'letter[lastname]',
    [DONATION_LETTER_RECIPIENT_EMAIL]: 'letter[recipientemail]',
    [DONATION_PRIVACY]: 'privacy',
    [DONATION_TERMS]: 'termsandconditions',
    [DONATION_NEWSLETTER]: 'newsletter',
    [DONATION_LETTER_RECIPIENT_ADDRESS]: 'letter[recipientaddress][address]',
    [DONATION_LETTER_RECIPIENT_ADDRESS_NUMBER]:
      'letter[recipientaddress][number]',
    [DONATION_LETTER_RECIPIENT_POSTAL_CODE]:
      'letter[recipientaddress][postalcode]',
    [DONATION_LETTER_RECIPIENT_CITY]: 'letter[recipientaddress][city]',
    [DONATION_LETTER_RECIPIENT_PROVINCE]: 'letter[recipientaddress][state]',
    [DONATION_LETTER_RECIPIENT_COUNTRY]: 'letter[recipientaddress][country]',
    [DONATION_PAYMENT_METHOD_ON_RECURRENCE_PAYMENT_TYPE]:
      'paymentContinuslyType',
    paymentClientId: 'client_id',
  }

  const fieldValuesMap = {
    [DONATION_TYPE]: {
      [FREE_DONATION_TYPE]: 'free',
      [IN_MEMORY_DONATION_TYPE]: 'memory',
    },
    [DONATION_AMOUNT]: {
      10: '10',
      20: '20',
      50: '50',
      [CUSTOM_DONATION_AMOUNT]: (value) => value.split('-')[2],
    },
    [DONATION_FREQUENCY]: {
      [ONE_TIME_DONATION_FREQUENCY]: 'singola',
      [MONTHLY_DONATION_FREQUENCY]: 'continuativa',
      [YEARLY_DONATION_FREQUENCY]: 'continuativa',
    },
    [DONATION_IN_MEMORY_LETTER_TO_RELATIVES]: {
      [YES]: '1',
      [NO]: '0',
    },
    [DONATOR_TYPE]: {
      [INDIVIDUAL]: 'persona',
      [COMPANY]: 'azienda',
    },
    [DONATOR_GENDER]: {
      [DEFAULT]: MALE,
      [MALE]: MALE,
      [FEMALE]: FEMALE,
    },
    [DONATOR_PAYMENT_METHOD]: {
      satispay: SERVER_PAYMENTS_METHODS.SATISPAY,
      postepay: SERVER_PAYMENTS_METHODS.POSTEPAY,
      paypal: SERVER_PAYMENTS_METHODS.PAYPAL,
      'credit-card': SERVER_PAYMENTS_METHODS.CREDITCARD,
    },
    [DONATION_PRIVACY]: {
      [TRUE]: '1',
      [FALSE]: '0',
      [YES]: '1',
      [NO]: '0',
    },
    [DONATION_TERMS]: {
      [TRUE]: '1',
      [FALSE]: '0',
      [YES]: '1',
      [NO]: '0',
    },
    [DONATION_NEWSLETTER]: {
      [TRUE]: '1',
      [FALSE]: '0',
      [YES]: '1',
      [NO]: '0',
    },
    [DONATION_PAYMENT_METHOD_ON_RECURRENCE_IBAN_IS_OWNER]: {
      [TRUE]: '1',
      [FALSE]: '0',
      [YES]: '1',
      [NO]: '0',
    },
    [DONATION_LETTER_PARTICIPANTS]: (participants) => {
      if (!Array.isArray(participants)) return ['MAP_NAME']
      return participants.map((p) => p.value)
    },
    [DONATOR_LETTER_FORMAT]: {
      [DIGITAL]: 'digital',
      [PAPER]: 'paper',
    },
    [DONATION_PAYMENT_METHOD_ON_RECURRENCE_PAYMENT_TYPE]: {
      [GENERIC_DONATION_TYPE]: 'generica',
      [PEDIATRIC_DONATION_TYPE]: 'pediatrica',
    },
    [DONATION_IN_MEMORY_RECEIVER_ANNIVERSARY_DATE]: formatDayMonthForApi,
  }

  const finalData = {
    client_id: paymentClientId,
    vertical: 'donation',
    id_campagna_competenza: campaignParams.ets_cmmk || null,
    id_segmento: campaignParams.ets_sgmt || null,
    id_soggetto: isNumeric(campaignParams.ets_sggt)
      ? campaignParams.ets_sggt
      : null,
    id_tipo_soggetto: isNumeric(campaignParams.ets_sggt_t)
      ? campaignParams.ets_sggt_t
      : null,
    id_occasione_contatto: '73',
  }

  const isCompany = data[DONATOR_TYPE] === 'company' ?? false

  // Transform data
  for (const [key, value] of Object.entries(data)) {
    const finalKey = fieldNameMap[key] || key
    const finalValue = mapValue(key, value, fieldValuesMap)

    if (finalKey.includes('[') && finalKey.includes(']')) {
      const path = finalKey.replace(/\[/g, '.').replace(/\]/g, '').split('.')
      createNestedObject(finalData, path, finalValue)
    } else {
      finalData[finalKey] = finalValue
    }
  }

  // Final cleanup and special mappings
  for (const [key, value] of Object.entries(finalData)) {
    if (key === 'fiscalcode' && value !== '') {
      finalData['taxid'] = value
    }

    if (value === '') {
      delete finalData[key]
    }

    if (key === 'companyemail' && isCompany) {
      finalData.email = value
      delete finalData[key]
    }

    if (key === 'letter') {
      if (!value['firstname']) {
        finalData['letter']['firstname'] = ''
      }
      if (value['senders'].length === 1 && value['senders'][0] === 'MAP_NAME') {
        finalData[key]['senders'] = [
          isCompany
            ? `${finalData['companyname']}`
            : `${finalData['firstname']} ${finalData['lastname']}`,
        ]
      }
    }
  }

  // Canonical attribution is id_campagna_competenza / id_segmento / id_soggetto /
  // id_tipo_soggetto. Drop the raw ETS keys so a stale form value cannot
  // override the landing-link segment resolved above.
  delete finalData.campaignParams
  delete finalData.ets_cmmk
  delete finalData.ets_sgmt
  delete finalData.ets_sggt
  delete finalData.ets_sggt_t

  return finalData
}

function mapValue(key, value, fieldValuesMap) {
  const valueMapping = fieldValuesMap[key]

  if (valueMapping !== undefined) {
    if (
      key === DONATION_AMOUNT &&
      value?.startsWith(`${CUSTOM_DONATION_AMOUNT}-`)
    ) {
      return valueMapping[CUSTOM_DONATION_AMOUNT](value)
    } else if (typeof valueMapping === 'function') {
      if (key === DONATION_LETTER_PARTICIPANTS) {
        const participants = valueMapping(value)
        return !participants || participants.length === 0
          ? ['MAP_NAME']
          : participants
      }
      return valueMapping(value)
    } else if (valueMapping[value] !== undefined) {
      return valueMapping[value]
    }
  }

  return value
}
